import React, { useEffect, useState } from 'react';
import { Menu, X, Phone, ShoppingCart, ChevronDown, MessageCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';
const servicesSubmenu = [{
  name: 'DTF Printing & Shipping',
  href: '/dtf-printing'
}, {
  name: 'Screen Printing',
  href: '/screen-printing'
}, {
  name: 'Custom Apparel',
  href: '/custom-apparel'
}, {
  name: 'Custom Banners',
  href: '/custom-banners'
}, {
  name: 'Business Cards',
  href: '/business-cards'
}, {
  name: 'Custom Key Tags',
  href: '/custom-key-tags'
}];
const aboutSubmenu = [{
  name: 'About Us',
  href: '/about'
}, {
  name: 'Catalog',
  href: '/catalog'
}, {
  name: 'FAQ',
  href: '/faq'
}];
const navLinks = [{
  name: 'DTF Transfers',
  href: '/dtf-printing'
}, {
  name: 'Screen Printing',
  href: '/screen-printing'
}, {
  name: 'Services',
  href: '#',
  hasSubmenu: true,
  submenuKey: 'services' as const
}, {
  name: 'About',
  href: '/about',
  hasSubmenu: true,
  submenuKey: 'about' as const
}, {
  name: 'Contact',
  href: '/contact'
}];
const submenus = {
  services: servicesSubmenu,
  about: aboutSubmenu
};
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState<string | null>(null);
  const [openMobileMenu, setOpenMobileMenu] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <header className={cn('sticky top-0 left-0 right-0 z-50 transition-all duration-300 border-b-0', isScrolled ? 'bg-atomic-black/95 backdrop-blur-md py-1' : 'bg-atomic-black/90 backdrop-blur-sm py-0.5')} data-id="element-211">
      <div className="container mx-auto px-4 md:px-6" data-id="element-212">
        <div className="flex items-center justify-between gap-4" data-id="element-213">
          {/* LEFT: Logo + Tagline */}
          <div className="flex items-center gap-3" data-id="element-214">
            <a href="/" className="flex items-center group pt-1" data-id="element-215">
              <img src="https://cdn.magicpatterns.com/uploads/nTH6rvmmBYe1TvsMQVLcBV/Atomic_Logo.png" alt="Print Atomic Logo" className="h-[100px] md:h-[120px] w-auto group-hover:drop-shadow-[0_0_8px_rgba(163,230,53,0.6)] transition-all" data-id="element-216" />
            </a>
            <div className="hidden md:block max-w-[140px] lg:max-w-[180px]" data-id="element-217">
              <p className="text-xs text-gray-500 leading-relaxed" data-id="element-218">
                DTF Printing & Shipping · Custom Apparel · Workwear
              </p>
            </div>
          </div>

          {/* CENTER: Primary Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-id="element-219">
            {navLinks.map(link => <div key={link.name} className="relative" data-id="element-220">
                {link.hasSubmenu && link.submenuKey ? <div onMouseEnter={() => setOpenDesktopMenu(link.submenuKey!)} onMouseLeave={() => setOpenDesktopMenu(null)} data-id="element-221">
                    <button className="text-sm font-medium text-gray-300 hover:text-atomic-green transition-colors flex items-center gap-1 cursor-pointer" data-id="element-222">
                      {link.name}
                      <ChevronDown size={16} className={cn('transition-transform', openDesktopMenu === link.submenuKey && 'rotate-180')} data-id="element-223" />
                    </button>

                    <AnimatePresence data-id="element-224">
                      {openDesktopMenu === link.submenuKey && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full left-0 mt-2 bg-atomic-charcoal border border-white/10 rounded-lg shadow-xl overflow-hidden min-w-[240px]" data-id="element-225">
                          {submenus[link.submenuKey].map(item => <a key={item.name} href={item.href} className="block px-4 py-3 text-sm text-gray-300 hover:text-atomic-green hover:bg-white/5 transition-colors" onClick={() => setOpenDesktopMenu(null)} data-id="element-226">
                              {item.name}
                            </a>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div> : <a href={link.href} className="text-sm font-medium text-gray-300 hover:text-atomic-green transition-colors" data-id="element-227">
                    {link.name}
                  </a>}
              </div>)}
          </nav>

          {/* RIGHT: Action Buttons */}
          <div className="flex items-center gap-2" data-id="element-228">
            <a href="tel:+15174149232" className="hidden lg:flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-atomic-green hover:bg-white/5 transition-colors" title="Call 517-414-9232" data-id="element-229">
              <Phone size={18} data-id="element-230" />
            </a>
            <a href="https://m.me/atomicMI" target="_blank" rel="noopener noreferrer" title="Facebook Message" aria-label="Facebook Message" className="hidden lg:inline-flex" data-id="element-231">
              <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-id="element-232">
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-233">
                  <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.2V22l2.95-1.62c.84.23 1.72.36 2.65.36h.25c5.64 0 9.75-4.13 9.75-9.66C20.75 6.13 17.64 2 12 2zm1.08 13.02l-2.5-2.67-4.87 2.67 5.36-5.69 2.56 2.67 4.81-2.67-5.36 5.69z" data-id="element-234" />
                </svg>
                Message
              </Button>
            </a>
            <a href="sms:+15174149232" className="hidden lg:inline-flex" data-id="element-235">
              <Button size="sm" className="gap-1.5" data-id="element-236">
                <MessageCircle size={15} data-id="element-237" />
                Text Us
              </Button>
            </a>
            <a href="/checkout" className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-atomic-green hover:bg-white/5 transition-colors" title="Checkout" data-id="element-238">
              <ShoppingCart size={20} data-id="element-239" />
            </a>
            <button className="lg:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-240">
              {isMobileMenuOpen ? <X size={24} data-id="element-241" /> : <Menu size={24} data-id="element-242" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-243">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden bg-atomic-charcoal border-b border-white/10 overflow-hidden" data-id="element-244">
            <div className="container mx-auto px-4 py-6 flex flex-col gap-4" data-id="element-245">
              <div className="text-xs text-gray-500 pb-4 border-b border-white/10" data-id="element-246">
                DTF Printing & Shipping · Custom Apparel · Workwear
              </div>

              {/* Mobile Quick Actions */}
              <div className="flex flex-col gap-3 pb-4 border-b border-white/10" data-id="element-247">
                <a href="sms:+15174149232" onClick={() => setIsMobileMenuOpen(false)} data-id="element-248">
                  <Button fullWidth className="gap-2" data-id="element-249">
                    <MessageCircle size={18} data-id="element-250" />
                    Text Us
                  </Button>
                </a>
                <a href="https://m.me/atomicMI" target="_blank" rel="noopener noreferrer" title="Facebook Message" aria-label="Facebook Message" onClick={() => setIsMobileMenuOpen(false)} data-id="element-251">
                  <Button fullWidth variant="outline" className="gap-2" data-id="element-252">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-253">
                      <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.2V22l2.95-1.62c.84.23 1.72.36 2.65.36h.25c5.64 0 9.75-4.13 9.75-9.66C20.75 6.13 17.64 2 12 2zm1.08 13.02l-2.5-2.67-4.87 2.67 5.36-5.69 2.56 2.67 4.81-2.67-5.36 5.69z" data-id="element-254" />
                    </svg>
                    Facebook Message
                  </Button>
                </a>
                <a href="tel:+15174149232" className="flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-atomic-green transition-colors py-2" onClick={() => setIsMobileMenuOpen(false)} data-id="element-255">
                  <Phone size={16} className="text-atomic-green" data-id="element-256" />
                  <span data-id="element-257">Call 517-414-9232</span>
                </a>
              </div>

              {/* Mobile Nav Links */}
              {navLinks.map(link => <div key={link.name} data-id="element-258">
                  {link.hasSubmenu && link.submenuKey ? <div data-id="element-259">
                      <button onClick={() => setOpenMobileMenu(openMobileMenu === link.submenuKey ? null : link.submenuKey!)} className="w-full flex items-center justify-between text-lg font-medium text-gray-300 hover:text-atomic-green transition-colors" data-id="element-260">
                        <span data-id="element-261">{link.name}</span>
                        <ChevronDown size={20} className={cn('transition-transform', openMobileMenu === link.submenuKey && 'rotate-180')} data-id="element-262" />
                      </button>
                      <AnimatePresence data-id="element-263">
                        {openMobileMenu === link.submenuKey && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="pl-4 mt-2 flex flex-col gap-2" data-id="element-264">
                            {submenus[link.submenuKey].map(item => <a key={item.name} href={item.href} className="text-base text-gray-400 hover:text-atomic-green transition-colors" onClick={() => {
                    setIsMobileMenuOpen(false);
                    setOpenMobileMenu(null);
                  }} data-id="element-265">
                                {item.name}
                              </a>)}
                          </motion.div>}
                      </AnimatePresence>
                    </div> : <a href={link.href} className="text-lg font-medium text-gray-300 hover:text-atomic-green transition-colors block" onClick={() => setIsMobileMenuOpen(false)} data-id="element-266">
                      {link.name}
                    </a>}
                </div>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}